/**
 * @license Copyright (c) 2014-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 * @ref CKEDITOR Sample for more settings/events https://github.com/ckeditor/ckeditor5-vue2/blob/master/sample/index.html
 */
import DecoupledDocumentEditor from '@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor.js';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment.js';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat.js';
// import Base64UploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/base64uploadadapter.js';
import Base64UploadAdapter from './adapters/base64uploadadapter.js';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote.js';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold.js';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder.js';
import CKFinderUploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter.js';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices.js';
import Comments from '@ckeditor/ckeditor5-comments/src/comments.js';
import AdjacentListsSupport from '@ckeditor/ckeditor5-list/src/list/adjacentlistssupport.js';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials.js';
import ExportPdf from '@ckeditor/ckeditor5-export-pdf/src/exportpdf.js';
import ExportWord from '@ckeditor/ckeditor5-export-word/src/exportword.js';
import Font from '@ckeditor/ckeditor5-font/src/font.js';
import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport.js';
import Heading from '@ckeditor/ckeditor5-heading/src/heading.js';
import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight.js';
import Image from '@ckeditor/ckeditor5-image/src/image.js';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption.js';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize.js';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle.js';
// import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar.js';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload.js';
import ImportWord from '@ckeditor/ckeditor5-import-word/src/importword.js';
import Indent from '@ckeditor/ckeditor5-indent/src/indent.js';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock.js';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic.js';
import Link from '@ckeditor/ckeditor5-link/src/link.js';
import List from '@ckeditor/ckeditor5-list/src/list.js';
import ListProperties from '@ckeditor/ckeditor5-list/src/listproperties.js';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed.js';
import Mention from '@ckeditor/ckeditor5-mention/src/mention.js';
import PageBreak from '@ckeditor/ckeditor5-page-break/src/pagebreak.js';
import Pagination from '@ckeditor/ckeditor5-pagination/src/pagination';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph.js';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice.js';
import PresenceList from '@ckeditor/ckeditor5-real-time-collaboration/src/presencelist.js';
import RealTimeCollaborativeComments from '@ckeditor/ckeditor5-real-time-collaboration/src/realtimecollaborativecomments.js';
import RealTimeCollaborativeEditing from '@ckeditor/ckeditor5-real-time-collaboration/src/realtimecollaborativeediting.js';
import RealTimeCollaborativeRevisionHistory from '@ckeditor/ckeditor5-real-time-collaboration/src/realtimecollaborativerevisionhistory.js';
import RealTimeCollaborativeTrackChanges from '@ckeditor/ckeditor5-real-time-collaboration/src/realtimecollaborativetrackchanges.js';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat.js';
import RevisionHistory from '@ckeditor/ckeditor5-revision-history/src/revisionhistory.js';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough.js';
import Table from '@ckeditor/ckeditor5-table/src/table.js';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties.js';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties.js';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar.js';
import TableColumnResize from '@ckeditor/ckeditor5-table/src/tablecolumnresize';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation.js';
import TrackChanges from '@ckeditor/ckeditor5-track-changes/src/trackchanges.js';
import TrackChangesData from '@ckeditor/ckeditor5-track-changes/src/trackchangesdata.js';
// import TodoList from '@ckeditor/ckeditor5-list/src/todolist.js';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline.js';

import AnnotationEditing from './annotation-editing';
import ClipboardInputTransformation from './clipboard-input-transformation';
import InsertAnchorUI from './add-anchor-button';
import TrackChangesSwitchUI from './track-changes-switch';
import ListMarkerSize from './list-marker-size/list-marker-size.js';

// A simple plugin that extends the remove format feature to consider links.
function RemoveFormatLinks(editor) {
  // Extend the editor schema and mark the "linkHref" model attribute as formatting.
  editor.model.schema.setAttributeProperties('linkHref', {
    isFormatting: true,
  });
}

/* Begin Custom CKEditor Build */
class CKEditorCS extends DecoupledDocumentEditor {}

// Plugins to include in the build.
CKEditorCS.builtinPlugins = [
  Alignment,
  Autoformat,
  Base64UploadAdapter,
  BlockQuote,
  Bold,
  CKFinder,
  CKFinderUploadAdapter,
  CloudServices,
  Comments,
  AdjacentListsSupport,
  Essentials,
  ExportPdf,
  ExportWord,
  Font,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  // ImageToolbar,
  ImageUpload,
  ImportWord,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  MediaEmbed,
  Mention,
  PageBreak,
  Pagination,
  Paragraph,
  PresenceList,
  PasteFromOffice,
  RealTimeCollaborativeComments,
  RealTimeCollaborativeEditing,
  RealTimeCollaborativeRevisionHistory,
  RealTimeCollaborativeTrackChanges,
  RevisionHistory,
  RemoveFormat,
  RemoveFormatLinks,
  Strikethrough,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  TableColumnResize,
  TextTransformation,
  TrackChanges,
  // TodoList,
  TrackChangesData,
  Underline,
  AnnotationEditing,
  ClipboardInputTransformation,
  InsertAnchorUI,
  TrackChangesSwitchUI,
  ListMarkerSize,
];

// Editor configuration.
CKEditorCS.defaultConfig = {
  language: 'en',
  // @ref CKEDITOR https://dashboard.ckeditor.com/organization/83708/premium-plugins/79942/manage
  licenseKey: 'ZDRkZ0wzUzdVTTFNTlNvb1B4ajJubXp6L25QaGdkY200L2Z3cloxZ0FRcjJ3UUdiV2hJSS1NakF5TkRBNE1qZz0=',
  toolbar: {
    items: [
      'comment',
      'trackChangesSwitch',
      'trackChanges',
      'revisionHistory', // If this is moved, update removeRevisionHistoryForCommentors in vue-app-document.js
      '|',
      // heading is the paragraph dropdown
      // 'heading',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'link',
      'imageUpload',
      '|',
      'alignment',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'outdent',
      'indent',
      'numberedList',
      'bulletedList',
      // '|',
      // 'undo',
      // 'redo',
      '|',
      'pageBreak',
      // 'previousPage',
      // 'nextPage',
      // 'pageNavigation',
      '|',
      //'todoList',
      'blockQuote',
      // 'highlight',
      'insertTable',
      // 'mediaEmbed',
      'removeFormat',
      'exportPdf',
      // 'exportWord',
      'importWord',
      'insertAnchor',
    ],
  },
  pagination: {
    // These configuration values must match the export to PDF configuration.

    // A4
    pageWidth: '21cm',
    pageHeight: '29.7cm',
    pageMargins: {
      top: '2cm',
      bottom: '2cm',
      right: '1.6cm',
      left: '1.6cm',
    },
  },
  highlight: {
    options: [
      {
        model: 'yellowMarker',
        class: 'marker-yellow',
        title: 'Yellow marker',
        color: 'var(--ck-highlight-marker-yellow)',
        type: 'marker',
      },
      {
        model: 'greenMarker',
        class: 'marker-green',
        title: 'Green marker',
        color: 'var(--ck-highlight-marker-green)',
        type: 'marker',
      },
      {
        model: 'redPen',
        class: 'pen-red',
        title: 'Red pen',
        color: 'var(--ck-highlight-pen-red)',
        type: 'pen',
      },
    ],
  },
  image: {
    toolbar: [
      // 'imageStyle:inline',
      // 'imageStyle:block',
      // 'imageStyle:side',
      // '|',
      // 'toggleImageCaption',
      // 'imageTextAlternative',
      // '|',
      // 'comment'
    ],
    // generates imageInline on insertImage command exec
    insert: {
      type: 'inline',
    },
  },
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableProperties',
      'tableCellProperties',
    ],
    tableToolbar: ['comment'],
  },
  link: {
    decorators: {
      openInNewTab: {
        mode: 'manual',
        label: 'Open in a new tab',
        attributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
    },
    defaultProtocol: 'http://',
  },
  mediaEmbed: {
    toolbar: ['comment'],
  },
  comments: {
    // Show the first comment and two most recent comments when collapsed.
    maxCommentsWhenCollapsed: 3,
    // Make comments shorter when collapsed.
    maxCommentCharsWhenCollapsed: 100,
    // Allow for up to 3 comments before collapsing.
    maxThreadTotalWeight: 600,
  },
  htmlSupport: {
    // @ref CKEDITOR https://ckeditor.com/docs/ckeditor5/latest/features/general-html-support.html
    allow: [
      {
        name: /.*/,
        attributes: true,
        classes: true,
        styles: true,
      },
    ],
    /* HTML features to disallow */
    disallow: [
      {
        name: 'script',
      },
      {
        name: 'iframe',
      },
      {
        name: /.*/,
        attributes: /on.*/,
      },
    ],
  },
  indentBlock: {
    offset: 1,
    unit: 'em',
  },
  fontSize: {
    options: [
      'default',
      9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 
      21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
    ],
    supportAllValues: true,
  },
  fontFamily: {
    options: [
      'default',
      'Arial, Helvetica, sans-serif',
      'Courier New, Courier, monospace',
      'Georgia, serif',
      'Lucida Sans Unicode, Lucida Grande, sans-serif',
      'Tahoma, Geneva, sans-serif',
      'Times New Roman, Times, serif',
      'Trebuchet MS, Helvetica, sans-serif',
      'Verdana, Geneva, sans-serif',
    ],
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
  importWord: {
    defaultStyles: true,
    // Feature configuration. @ref https://ckeditor.com/docs/ckeditor5/latest/features/import-word.html
  },
  exportPdf: {
    // include fonts with imports at top,
    // as imported fonts in pdf-converter.css will be ignored
    stylesheets: [
      'EDITOR_STYLES',
      '/src/assets/css/ckeditor/pdf-converter-fonts.css',
      '/src/assets/css/ckeditor/pdf-converter.css',
    ],
    fileName: 'document.pdf',
    converterUrl: 'https://pdf-converter.cke-cs.com/v1/convert',

    // These configuration values must match Pagination configuration.
    converterOptions: {
      format: 'A4',
      margin_top: '2cm',
      margin_bottom: '2cm',
      margin_right: '1.6cm',
      margin_left: '1.6cm',
      page_orientation: 'portrait',
      header_html: undefined,
      footer_html: undefined,
      header_and_footer_css: undefined,
      wait_for_network: true,
      wait_time: 0,
    },
    dataCallback: (editor) => editor.getData(),
  },
};

export default CKEditorCS;
